// ╔════════════════════════════════════════════════════════════════════════════╗
// ║  PARENTQUIZPAGE.JSX - VERSION: 2025.12.30.2230                             ║
// ╚════════════════════════════════════════════════════════════════════════════╝
// ============================================================================
// 📦 VERSION: 2025.12.30.2230
// Parent quiz page - shows child quizzes for category pages like "All About Trains"
// ============================================================================
// CHANGELOG:
// 2025.12.30.2230 - Initial version - parent category page with child quiz selector
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = 'https://api.traktag.com/api';

const ParentQuizPage = ({ parentGame, onSelectChildQuiz }) => {
  const [childQuizzes, setChildQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchChildQuizzes = async () => {
      if (!parentGame?.game_id) return;
      
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/games/${parentGame.game_id}/children`);
        if (response.ok) {
          const children = await response.json();
          setChildQuizzes(children);
          console.log(`📂 Loaded ${children.length} child quizzes for ${parentGame.name}`);
        }
      } catch (err) {
        console.error('Failed to load child quizzes:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchChildQuizzes();
  }, [parentGame]);

  const handleSelectQuiz = (quiz) => {
    if (onSelectChildQuiz) {
      onSelectChildQuiz(quiz);
    } else {
      // Navigate to the child quiz
      navigate(`/quiz/${quiz.slug}`);
    }
  };

  if (!parentGame) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-6 text-center">
        <img 
          src="/traktag-logo.png" 
          alt="TRAKtag" 
          className="h-12 mx-auto mb-4"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <h1 className="text-white text-3xl font-black mb-2">{parentGame.name}</h1>
        {parentGame.description && (
          <p className="text-blue-100 text-sm max-w-md mx-auto">{parentGame.description}</p>
        )}
      </div>

      {/* Child Quizzes Grid */}
      <div className="max-w-2xl mx-auto p-4">
        <h2 className="text-yellow-400 text-lg font-bold mb-4 flex items-center gap-2">
          <span>📚</span> Choose a Quiz
        </h2>

        {loading ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-2">⏳</div>
            <p className="text-gray-400">Loading quizzes...</p>
          </div>
        ) : childQuizzes.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-2">📭</div>
            <p className="text-gray-400">No quizzes available yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {childQuizzes.map((quiz) => (
              <button
                key={quiz.game_id}
                onClick={() => handleSelectQuiz(quiz)}
                className="w-full bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-teal-500 rounded-xl p-4 text-left transition-all flex items-center gap-4"
              >
                {/* Quiz Icon */}
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-600 rounded-xl flex items-center justify-center text-3xl flex-shrink-0">
                  {quiz.icon_url ? (
                    <img src={quiz.icon_url} alt="" className="w-12 h-12 object-contain" />
                  ) : (
                    '🎯'
                  )}
                </div>

                {/* Quiz Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-bold text-lg truncate">{quiz.name}</h3>
                  {quiz.description && (
                    <p className="text-gray-400 text-sm truncate">{quiz.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1">
                    {quiz.play_count > 0 && (
                      <span className="text-teal-400 text-xs">🎮 {quiz.play_count} plays</span>
                    )}
                    {quiz.featured && (
                      <span className="text-yellow-400 text-xs">⭐ Featured</span>
                    )}
                  </div>
                </div>

                {/* Arrow */}
                <div className="text-teal-400 text-2xl">
                  ▶
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="w-full mt-6 bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium py-3 px-6 rounded-xl transition-all"
        >
          ← Back to Home
        </button>
      </div>
    </div>
  );
};

export default ParentQuizPage;
